import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { addDays, normalizeTimeSlots, parseDisplayTime } from '@/utils/normalizeTimeSlots';

/**
 * Availability categories are derived against a fixed zone rather than the
 * visitor's browser zone, so a provider's "Available Now" window is identical
 * for every visitor regardless of where they are browsing from.
 */
const AVAILABILITY_TIME_ZONE = 'America/New_York';

/** Minutes inside the "now" window — a slot at or under this is `"now"`. */
const AVAILABLE_NOW_WINDOW_MINUTES = 60;

const NO_AVAILABILITY = { category: 'none', nextAvailableTime: null };

/**
 * Reads the current instant in `AVAILABILITY_TIME_ZONE` via `Intl.DateTimeFormat`
 * (`moment-timezone` is not a project dependency).
 *
 * @returns {{date: string, minutesSinceMidnight: number}} The zone's current
 *     calendar date as `YYYY-MM-DD` and its time of day in minutes.
 */
const getZonedNow = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: AVAILABILITY_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(new Date());
    const zoned = parts.reduce((acc, { type, value }) => ({ ...acc, [type]: value }), {});
    // Some ICU versions report midnight as hour "24" under hour12: false.
    const hours = parseInt(zoned.hour, 10) % 24;

    return {
        date: `${zoned.year}-${zoned.month}-${zoned.day}`,
        minutesSinceMidnight: hours * 60 + parseInt(zoned.minute, 10),
    };
};

const usePhysicianBioAvailabilityDate = ({
    npi,
    newPatientAppointmentIds,
    departmentIds = null,
    range = 15,
    firstAvailabilityDate = null,
    nextAvailabilityDate = null,
}) => {
    const [availabilityDate, setAvailabilityDate] = useState(null);
    const [timeSlots, setTimeSlots] = useState(null);
    const [startDate, endDate, visitTypeIds] = useMemo(() => {
        const nowMoment = moment(new Date());
        const selectedStartDate = firstAvailabilityDate ? moment(firstAvailabilityDate) : null;
        const baseDate =
            selectedStartDate && selectedStartDate.isValid()
                ? selectedStartDate.isBefore(nowMoment)
                    ? nowMoment
                    : selectedStartDate
                : nowMoment;
        const startDate = baseDate.format('YYYY-MM-DD');
        const endDate = baseDate.clone().add(range, 'days').format('YYYY-MM-DD');
        const visitTypeIds = newPatientAppointmentIds?.filter((item) => item).join(',');
        return [startDate, endDate, visitTypeIds];
    }, [newPatientAppointmentIds, range, firstAvailabilityDate]);
    const fetchAvailabilityData = useCallback(
        async (npi, visitTypeIds, startDate, endDate, signal = null) => {
            const path = '/bin/ahs/providerAvailability';
            const paramsObj = {
                npi,
                visitTypeIds,
                startDate,
                endDate,
            };
            const newSearchParams = new URLSearchParams();
            for (const param in paramsObj) {
                if (paramsObj[param]) {
                    newSearchParams.set(param, paramsObj[param]);
                }
            }
            const url = `${path}?${newSearchParams.toString()}`;
            const requestHeaders = new Headers();
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            requestHeaders.append('X-TIMEZONE', timeZone);

            const response = await fetch(url, { method: 'GET', headers: requestHeaders, signal });

            if (!response.ok) {
                throw new Error(`Error: Status ${response.status}, ${response.statusText}`);
            }

            const data = await response.json();

            return data.timeSlots;
        },
        [],
    );

    useEffect(() => {
        const controller = new AbortController();
        const getAvailabilityDate = async () => {
            if (!npi) {
                return;
            }
            try {
                const timeslots = await fetchAvailabilityData(
                    npi,
                    visitTypeIds,
                    startDate,
                    endDate,
                    controller.signal,
                );
                if (typeof timeslots === 'object' && Object.keys(timeslots).length > 0) {
                    const firstDate = Object.keys(timeslots)[0];
                    setAvailabilityDate(firstDate);
                    setTimeSlots(normalizeTimeSlots(timeslots, firstDate, addDays(firstDate, 3)));
                } else {
                    // API returned empty timeslots - no availability
                    setTimeSlots({});
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    // Fetch was aborted (component unmounted) - expected behavior
                    return;
                }
                setAvailabilityDate(null);
                setTimeSlots(null);
            }
        };

        getAvailabilityDate();

        return () => controller.abort();
    }, [
        npi,
        visitTypeIds,
        startDate,
        endDate,
        fetchAvailabilityData,
        firstAvailabilityDate,
        nextAvailabilityDate,
    ]);

    const { category, nextAvailableTime } = useMemo(() => {
        if (!availabilityDate || !timeSlots?.length) {
            return NO_AVAILABILITY;
        }

        const now = getZonedNow();
        if (availabilityDate > now.date) {
            // The first date the backend returned is in the future; the date
            // itself is already exposed as `availabilityDate`.
            return { category: 'soon-day', nextAvailableTime: null };
        }
        if (availabilityDate < now.date) {
            // Stale data (e.g. a pre-midnight fetch resolving after rollover) — the
            // returned date is already in the past, so treat it as no availability.
            return NO_AVAILABILITY;
        }

        // `normalizeTimeSlots()` anchors index 0 at `availabilityDate` and returns
        // each day's times pre-sorted, so the earliest slot today is the first entry.
        const [earliestTime] = timeSlots[0]?.times ?? [];
        const earliestMinutes = parseDisplayTime(earliestTime);
        if (earliestMinutes === null) {
            return NO_AVAILABILITY;
        }

        const minutesUntilSlot = earliestMinutes - now.minutesSinceMidnight;
        if (minutesUntilSlot >= 0 && minutesUntilSlot <= AVAILABLE_NOW_WINDOW_MINUTES) {
            // `nextAvailableTime` is populated here (not `null`) so a non-virtual
            // provider in this bucket can render the time — Virtual Care ignores it
            // and renders "Available Now" instead (see the display helpers).
            return { category: 'now', nextAvailableTime: earliestTime };
        }

        return { category: 'soon-time', nextAvailableTime: earliestTime };
    }, [availabilityDate, timeSlots]);

    const available = useMemo(() => {
        // Default to true during loading (timeSlots === null) for backwards compatibility
        if (timeSlots === null) {
            return true;
        }
        return Object.keys(timeSlots).length > 0;
    }, [timeSlots]);

    return { availabilityDate, timeSlots, category, nextAvailableTime, available };
};

export default usePhysicianBioAvailabilityDate;
